/*
 **	Device Settings Panel Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React, { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useUnitPreference } from '../../contexts/UnitPreferenceContext'
import { convertPressure, getPressureInputStep } from '../../utils/unitConversion'
import { getDeviceById, sendDeviceCommand, type Device } from '../../services/api'
import { OCAColors, Typography, Spacing } from '../../theme/colors'
import { IOSSlideUpPanel } from './IOSSlideUpPanel'
import { IOSListSection } from './IOSListSection'

interface DeviceSettingsPanelProps {
  device: Device | null
  isOpen: boolean
  onClose: () => void
}

interface DeviceSettings {
  zone1_name: string
  screen_timeout: boolean
  sleep_timer_powered: number
  sleep_timer_battery: number
  measure_sec: number
  auto_z_sec: number
  measure_unit: number
  dp0wt: number
  dp0at: number
  wifi_ssid: string
  wifi_password: string
  wifi_security: string
}

export const DeviceSettingsPanel: React.FC<DeviceSettingsPanelProps> = ({
  device,
  isOpen,
  onClose,
}) => {
  const { getAccessToken, isAuthenticated } = useAuth()
  const { pressureUnit } = useUnitPreference()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  // Form state
  const [settings, setSettings] = useState<Partial<DeviceSettings>>({})
  const [initialSettings, setInitialSettings] = useState<Partial<DeviceSettings>>({})

  useEffect(() => {
    if (isOpen && device && isAuthenticated) {
      loadDeviceSettings()
    }
  }, [isOpen, device, isAuthenticated])

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(initialSettings)
    setHasChanges(changed)
  }, [settings, initialSettings])

  const loadDeviceSettings = async () => {
    if (!device || !isAuthenticated) return

    try {
      setIsLoading(true)
      setError('')

      const token = await getAccessToken()
      if (!token) {
        setError('Unable to get access token')
        return
      }

      // Get full device details with properties
      const deviceDetails = await getDeviceById(device.deviceId, token)

      // Parse properties field (JSON string)
      let properties: any = {}
      if (deviceDetails.properties) {
        try {
          properties = JSON.parse(deviceDetails.properties)
        } catch (e) {
          console.warn('Failed to parse device properties:', e)
        }
      }

      // Round to 4 decimal places
      const round4dp = (value: number | undefined) => {
        if (value === undefined || value === null) return undefined
        return Math.round(value * 10000) / 10000
      }

      // Get device data for thresholds (from real-time or cached data)
      // For now we'll use device properties, but ideally fetch from device data store
      const loadedSettings: Partial<DeviceSettings> = {
        zone1_name: properties.zone1_name || '',
        screen_timeout: properties.screen_timeout === 1,
        sleep_timer_powered: properties.sleep_timer_powered || 300,
        sleep_timer_battery: properties.sleep_timer_battery || 60,
        measure_sec: properties.measure_sec || 60,
        auto_z_sec: properties.auto_z_sec || 3600,
        measure_unit: properties.measure_unit !== undefined ? properties.measure_unit : 0,
        dp0wt: round4dp(properties.dp0wt) || 0.02,
        dp0at: round4dp(properties.dp0at) || 0.05,
        wifi_ssid: properties.wifi_ssid || '',
        wifi_password: properties.wifi_password || '',
        wifi_security: properties.wifi_security || 'WPA2',
      }

      setSettings(loadedSettings)
      setInitialSettings(loadedSettings)
    } catch (err) {
      setError('Failed to load device settings')
      console.error('Error loading device settings:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!device || !hasChanges) return

    try {
      setIsSaving(true)
      setError('')

      const token = await getAccessToken()
      if (!token) {
        setError('Unable to get access token')
        return
      }

      // Build device settings object with only changed fields
      const deviceSettings: any = {}

      // Helper to check if value changed
      const hasChanged = (key: keyof DeviceSettings) => {
        return settings[key] !== initialSettings[key]
      }

      // Zone Configuration
      if (hasChanged('zone1_name') && settings.zone1_name !== undefined) {
        deviceSettings.zone1_name = settings.zone1_name
      }

      // WiFi Settings
      if (hasChanged('wifi_ssid') && settings.wifi_ssid !== undefined) {
        deviceSettings.wifi_ssid = settings.wifi_ssid
      }
      if (hasChanged('wifi_password') && settings.wifi_password !== undefined) {
        deviceSettings.wifi_password = settings.wifi_password
      }
      if (hasChanged('wifi_security') && settings.wifi_security !== undefined) {
        deviceSettings.wifi_security = settings.wifi_security
      }

      // Screen & Power Management
      if (hasChanged('screen_timeout') && settings.screen_timeout !== undefined) {
        deviceSettings.screen_timeout = settings.screen_timeout ? 1 : 0
      }
      if (hasChanged('sleep_timer_powered') && settings.sleep_timer_powered !== undefined) {
        deviceSettings.sleep_timer_powered = settings.sleep_timer_powered
      }
      if (hasChanged('sleep_timer_battery') && settings.sleep_timer_battery !== undefined) {
        deviceSettings.sleep_timer_battery = settings.sleep_timer_battery
      }

      // Pressure Thresholds
      if (hasChanged('dp0wt') && settings.dp0wt !== undefined) {
        deviceSettings.dp0wt = settings.dp0wt
      }
      if (hasChanged('dp0at') && settings.dp0at !== undefined) {
        deviceSettings.dp0at = settings.dp0at
      }

      // Measurement Settings
      if (hasChanged('measure_sec') && settings.measure_sec !== undefined) {
        deviceSettings.measure_sec = settings.measure_sec
      }
      if (hasChanged('auto_z_sec') && settings.auto_z_sec !== undefined) {
        deviceSettings.auto_z_sec = settings.auto_z_sec
      }
      if (hasChanged('measure_unit') && settings.measure_unit !== undefined) {
        deviceSettings.measure_unit = settings.measure_unit
      }

      // Send settings command
      const success = await sendDeviceCommand(
        token,
        device.deviceId,
        JSON.stringify(deviceSettings),
        `Update device settings for ${device.name}`
      )

      if (success) {
        // Update initial settings to current settings
        setInitialSettings({ ...settings })
        setHasChanges(false)
        onClose()
      } else {
        setError('Failed to send device settings command')
      }
    } catch (err) {
      setError('Failed to save device settings')
      console.error('Error saving device settings:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirmed) return
    }
    onClose()
  }

  const updateSetting = <K extends keyof DeviceSettings>(key: K, value: DeviceSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  if (!device) return null

  return (
    <IOSSlideUpPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Device Settings"
      height="90vh"
      headerAction={
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px 16px',
            cursor: hasChanges && !isSaving ? 'pointer' : 'default',
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.semibold,
            color: hasChanges && !isSaving ? OCAColors.primary : OCAColors.tertiaryLabel,
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      }
    >
      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: Spacing.xl,
          color: OCAColors.secondaryLabel,
        }}>
          Loading device settings...
        </div>
      ) : (
        <div style={{ paddingBottom: Spacing.xl }}>
          {error && (
            <div style={{
              padding: Spacing.md,
              margin: Spacing.md,
              backgroundColor: OCAColors.danger + '15',
              borderRadius: '8px',
              color: OCAColors.danger,
              fontSize: Typography.fontSize.sm,
            }}>
              {error}
            </div>
          )}

          {/* Monitoring & Thresholds Section */}
          <IOSListSection title="Monitoring & Thresholds">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
            }}>
              {/* Zone Name */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Zone 1 Name
                </label>
                <input
                  type="text"
                  value={settings.zone1_name || ''}
                  onChange={(e) => updateSetting('zone1_name', e.target.value)}
                  placeholder="e.g., Clean Room A"
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Warning Threshold */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Warning Threshold ({pressureUnit})
                </label>
                <input
                  type="number"
                  step={getPressureInputStep(pressureUnit)}
                  value={settings.dp0wt !== undefined ? convertPressure(settings.dp0wt, 'inWC', pressureUnit) : ''}
                  onChange={(e) => {
                    const inputValue = parseFloat(e.target.value) || 0
                    // Convert from selected unit back to inWC for storage
                    const inWCValue = convertPressure(inputValue, pressureUnit, 'inWC')
                    updateSetting('dp0wt', inWCValue)
                  }}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Alarm Threshold */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Alarm Threshold ({pressureUnit})
                </label>
                <input
                  type="number"
                  step={getPressureInputStep(pressureUnit)}
                  value={settings.dp0at !== undefined ? convertPressure(settings.dp0at, 'inWC', pressureUnit) : ''}
                  onChange={(e) => {
                    const inputValue = parseFloat(e.target.value) || 0
                    // Convert from selected unit back to inWC for storage
                    const inWCValue = convertPressure(inputValue, pressureUnit, 'inWC')
                    updateSetting('dp0at', inWCValue)
                  }}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Measurement Interval */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Measurement Interval (seconds)
                </label>
                <input
                  type="number"
                  value={settings.measure_sec || ''}
                  onChange={(e) => updateSetting('measure_sec', parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Measurement Units */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Measurement Units
                </label>
                <select
                  value={settings.measure_unit || 0}
                  onChange={(e) => updateSetting('measure_unit', parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                >
                  <option value={0}>inWC</option>
                  <option value={1}>Pa</option>
                  <option value={2}>kPa</option>
                </select>
              </div>

              {/* Auto-Zero Interval */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Auto-Zero Interval (seconds)
                </label>
                <input
                  type="number"
                  value={settings.auto_z_sec || ''}
                  onChange={(e) => updateSetting('auto_z_sec', parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>
            </div>
          </IOSListSection>

          {/* Power Management Section */}
          <IOSListSection title="Power Management">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
            }}>
              {/* Screen Timeout */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <label style={{
                  fontSize: Typography.fontSize.base,
                  color: OCAColors.label,
                }}>
                  Screen Timeout Enabled
                </label>
                <input
                  type="checkbox"
                  checked={settings.screen_timeout || false}
                  onChange={(e) => updateSetting('screen_timeout', e.target.checked)}
                  style={{
                    width: '51px',
                    height: '31px',
                  }}
                />
              </div>

              {/* Sleep Timer Powered */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Sleep Timer (Powered, seconds)
                </label>
                <input
                  type="number"
                  value={settings.sleep_timer_powered || ''}
                  onChange={(e) => updateSetting('sleep_timer_powered', parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Sleep Timer Battery */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Sleep Timer (Battery, seconds)
                </label>
                <input
                  type="number"
                  value={settings.sleep_timer_battery || ''}
                  onChange={(e) => updateSetting('sleep_timer_battery', parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>
            </div>
          </IOSListSection>

          {/* WiFi Settings Section */}
          <IOSListSection title="WiFi Settings">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
            }}>
              {/* SSID */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Network SSID
                </label>
                <input
                  type="text"
                  value={settings.wifi_ssid || ''}
                  onChange={(e) => updateSetting('wifi_ssid', e.target.value)}
                  placeholder="WiFi network name"
                  maxLength={32}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Password */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Network Password
                </label>
                <input
                  type="password"
                  value={settings.wifi_password || ''}
                  onChange={(e) => updateSetting('wifi_password', e.target.value)}
                  placeholder="WiFi network password"
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                />
              </div>

              {/* Security Type */}
              <div style={{
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
              }}>
                <label style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.label,
                  display: 'block',
                  marginBottom: Spacing.xs,
                }}>
                  Security Type
                </label>
                <select
                  value={settings.wifi_security || 'WPA2'}
                  onChange={(e) => updateSetting('wifi_security', e.target.value)}
                  style={{
                    width: '100%',
                    padding: Spacing.sm,
                    fontSize: Typography.fontSize.base,
                    border: `1px solid ${OCAColors.separator}`,
                    borderRadius: '8px',
                    backgroundColor: OCAColors.systemGroupedBackground,
                    color: OCAColors.label,
                  }}
                >
                  <option value="WPA3">WPA3</option>
                  <option value="WPA2">WPA2</option>
                  <option value="WEP">WEP</option>
                  <option value="Open">Open</option>
                </select>
              </div>
            </div>
          </IOSListSection>
        </div>
      )}
    </IOSSlideUpPanel>
  )
}
